import { Box, FormControlLabel, MenuItem, Stack, Switch, TextField, Typography } from '@mui/material';

import type { FormBorderStyle, FormPadding, ShapeScale } from '@/types/database';
import { PreviewCard } from './PreviewCard';
import { TwoColumn } from './TwoColumn';
import type { SiteStylesScreenProps } from './types';

const SHAPE_OPTIONS: { value: ShapeScale; label: string }[] = [
  { value: 'square', label: 'Square' },
  { value: 'soft', label: 'Soft' },
  { value: 'rounded', label: 'Rounded' },
  { value: 'pill', label: 'Pill' },
];

const BORDER_OPTIONS: { value: FormBorderStyle; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'outline', label: 'Outline' },
];

const PADDING_OPTIONS: { value: FormPadding; label: string }[] = [
  { value: 'sm', label: 'Small' },
  { value: 'md', label: 'Medium' },
  { value: 'lg', label: 'Large' },
];

export function FormsScreen({ draft, updateTheme }: SiteStylesScreenProps) {
  const { forms } = draft.theme_config;

  const setForms = (patch: Partial<typeof forms>) => updateTheme({ ...draft.theme_config, forms: { ...forms, ...patch } }, { immediate: true });

  return (
    <Stack spacing={2.5} sx={{ p: 2.5 }}>
      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, display: 'block' }}>
        Forms
      </Typography>

      <TwoColumn
        controls={
          <>
            <TextField
              select
              label="Field Shape"
              size="small"
              value={forms.fieldShape}
              onChange={(event) => setForms({ fieldShape: event.target.value as ShapeScale })}
              fullWidth
            >
              {SHAPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <FormControlLabel
              control={<Switch checked={forms.fieldFill} onChange={(event) => setForms({ fieldFill: event.target.checked })} />}
              label="Field Fill"
            />

            <TextField
              select
              label="Border"
              size="small"
              value={forms.border}
              onChange={(event) => setForms({ border: event.target.value as FormBorderStyle })}
              fullWidth
            >
              {BORDER_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Padding"
              size="small"
              value={forms.padding}
              onChange={(event) => setForms({ padding: event.target.value as FormPadding })}
              fullWidth
            >
              {PADDING_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </>
        }
        preview={
          <PreviewCard themeConfig={draft.theme_config}>
            <Stack direction="row" spacing={1.5}>
              <TextField label="Text" sx={{ flex: 1 }} />
              <Box
                component="button"
                type="button"
                sx={{ bgcolor: 'text.primary', color: 'background.paper', border: 'none', borderRadius: 1, px: 2, cursor: 'default' }}
              >
                Option
              </Box>
            </Stack>
          </PreviewCard>
        }
      />
    </Stack>
  );
}
